// Import required libraries
import axios from "axios";
// Import actions
import { logout as logoutAction } from "../../redux/slice/authSlice";
// Import store
import { store } from "../../redux/store";

import APP_CONSTANTS, {
  API_ENDPOINTS,
  MESSAGES,
  errorMessages,
} from "../../utils/config";
import { updateAlertInfo } from "../../redux/slice/userSlice";

const { CancelToken } = axios;
const cancelSource = CancelToken.source();

const instance = axios.create({
  withCredentials: true,
  cancelToken: cancelSource.token,
});

// Making a mapping for error messages
var errorMessagesMapping = {};
errorMessages.forEach((errorObj) => {
  errorMessagesMapping[errorObj.statusCode] = errorObj.message;
});

// Add a request interceptor
instance.interceptors.request.use(
  function (config) {
    config.headers["Accept"] = `application/json`;
    config.headers["Content-Type"] = `application/json`;

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Add a response interceptor
instance.interceptors.response.use(
  function (response) {
    const data = response.data;
    const status = data?.status?.statusCode;
    const statusText = data?.status?.statusMessage;
    const errorText = statusText || errorMessagesMapping[status];
    let errorFlag = false;
    switch (status) {
      case "nam-200":
        errorFlag = false;
        break;
      case "nam-403":
        errorFlag = true;
        break;
      case "nam-404":
        errorFlag = true;
        break;
      case "nam-408":
        errorFlag = true;
        break;
      case "nam-409":
        errorFlag = true;
        break;
      case "nam-413":
        errorFlag = true;
        break;
      case "nam-415":
        errorFlag = true;
        break;
      case "nam-500":
        errorFlag = true;
        break;
      default:
        errorFlag = false;
    }
    return errorFlag ? Promise.reject({ error: errorText }) : response;
  },
  function (error) {
    const response = error.response;
    const status = response?.status;
    var statusText = response?.statusText;
    switch (status) {
      case 400:
        statusText = "Bad request";
        break;
      case 401:
        const path = window.location.pathname;
        const search = window.location.search;
        cancelSource.cancel("Operation canceled due to unauthorized request.");
        window.location.href = `${API_ENDPOINTS.signinUrl}?redirect_path=${
          path + search
        }`;

        store.dispatch(logoutAction());
        break;
      case 403:
        store.dispatch(
          updateAlertInfo({
            message: MESSAGES.UNAUTHORIZED,
            open: true,
          })
        );
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
        break;
      case 404:
        statusText = "Not found";
        break;
      case 500:
        statusText = "Bad request";
        break;
      default:
        console.log(statusText);
    }

    return Promise.reject(error);
  }
);

// * SAMPLE FUNCTION
export function getUserInfo(props = {}) {
  const defaultUrl = API_ENDPOINTS.userinfoUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getSignupTemplate(props = {}) {
  const defaultUrl = API_ENDPOINTS.signupTemplateUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchReports(props = {}) {
  const defaultUrl = API_ENDPOINTS.reportsUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchFavReports(props = {}) {
  const defaultUrl = API_ENDPOINTS.favouritesUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function postUserInfo(props = {}) {
  const defaultUrl = API_ENDPOINTS.userinfoUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function updateUserInfo(props = {}) {
  const defaultUrl = API_ENDPOINTS.userinfoUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function fetchTemplateData(props = {}) {
  const defaultUrl = API_ENDPOINTS.templateUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchOwnersData(props = {}) {
  const defaultUrl = API_ENDPOINTS.oktaSearchUsersUrl;
  // Making variables from props
  const {
    url = defaultUrl,
    payload = { search_string: "" },
    headers = {},
  } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchOwnersData2(props = {}) {
  const defaultUrl = API_ENDPOINTS.searchoktaAllUsers;
  // Making variables from props
  const {
    url = defaultUrl,
    payload = { search_string: "" },
    headers = {},
  } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchOwnersData3(props = {}) {
  const defaultUrl = API_ENDPOINTS.searchoktaAllUsers;
  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  return instance.post(url, payload, { headers, signal }); // Pass signal here
}

export function getGoldTableReports(props = {}) {
  const defaultUrl = API_ENDPOINTS.goldTableReportsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function registerDataset(props = {}) {
  const defaultUrl = API_ENDPOINTS.datasetRegisterUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function registerReport(props = {}) {
  const defaultUrl = API_ENDPOINTS.reportRegisterUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function updateRegisterReport(props = {}) {
  const defaultUrl = API_ENDPOINTS.subApplicationEditDetailsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function updateGroupDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.getApplicationdetailsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function registerStandAloneApp(props = {}) {
  const defaultUrl = API_ENDPOINTS.applicationUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getMyAppsData(props = {}) {
  const defaultUrl = API_ENDPOINTS.myAppsUrl;

  // Making variables from props
  const { params = {}, headers = {}, signal } = props;
  const urlWithParams = defaultUrl + `?app_sub_type=` + params?.app_sub_type;
  const promise = instance.get(urlWithParams, { headers, signal });
  return promise;
}

export function addApplicationInFavourites(props = {}) {
  const defaultUrl = API_ENDPOINTS.favouritesUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function removeApplicationFromFavourites(props = {}) {
  const defaultUrl = API_ENDPOINTS.favouritesUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.delete(url, { headers, data: payload });
  return promise;
}

export function fetchApplicationDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.applicationDetailsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchApplicationDetailsAccess(props = {}) {
  const defaultUrl = API_ENDPOINTS.applicationDetailsAccessUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function endorseApplication(props = {}) {
  const defaultUrl = API_ENDPOINTS.endorsementUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchApplicationSuggestionsData(props = {}) {
  const defaultUrl = API_ENDPOINTS.applicationSuggestionsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchSearchApplication(props = {}) {
  const defaultUrl = API_ENDPOINTS.searchUrl;

  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function logout(props = {}) {
  const defaultUrl = API_ENDPOINTS.logout;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchDataLineage(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataLineageUrl;
  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchReportLineage(props = {}) {
  const defaultUrl = API_ENDPOINTS.reportLineageUrl;
  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getApplicationdetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.getApplicationdetailsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function putApplicationdetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.getApplicationdetailsUrl;
  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function fetchFAQs(props = {}) {
  const defaultUrl = API_ENDPOINTS.faqUrl;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchResources(props = {}) {
  const defaultUrl = API_ENDPOINTS.resourcesUrl;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchApprovals(props = {}) {
  const defaultUrl = API_ENDPOINTS.approvalsUrl;

  const { params = {}, headers = {} } = props;
  let urlWithParams = `${defaultUrl}?type=${params.type}&offset=${params.offset}&limit=${params.limit}`;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function fetchApprovalsDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.approvalsDetailsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function putApprovalsActionsUrl(props = {}) {
  const defaultUrl = API_ENDPOINTS.approvalsActionsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function requestAccess(props = {}) {
  const defaultUrl = API_ENDPOINTS.requestAccessUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function dataModelRequestAccess(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataModelRequestAccessUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function putAccessManagement(props = {}) {
  const defaultUrl = API_ENDPOINTS.accessManagementUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function getAccessManagement(props = {}) {
  const defaultUrl = API_ENDPOINTS.accessManagementUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function putPendingRequests(props = {}) {
  const defaultUrl = API_ENDPOINTS.pendingRequestsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}
export function getPendingRequests(props = {}) {
  const defaultUrl = API_ENDPOINTS.pendingRequestsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}
export function getGroupPendingRequests(props = {}) {
  const defaultUrl = API_ENDPOINTS.groupPendingRequestsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getOktaSearchUsers(props = {}) {
  const defaultUrl = API_ENDPOINTS.oktaSearchUsersUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getOktaSearchGroups(props = {}) {
  const defaultUrl = API_ENDPOINTS.oktaSearchGroupsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function updatePublishStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.publishUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function updateApprovalStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.appApprovalUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function updateAccessStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.accessUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function confirmAccessStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.confirmAccessStatusUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function getAppUserList(props = {}) {
  const defaultUrl = API_ENDPOINTS.appUserListUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  // const urlWithParams = url + `?app_id=` + params?.app_id;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getUsageMetrics(props = {}) {
  const defaultUrl = API_ENDPOINTS.usageMetricsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getTopUsersList(props = {}) {
  const defaultUrl = API_ENDPOINTS.getTopUsersUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getAppRanking(props = {}) {
  const defaultUrl = API_ENDPOINTS.getAppRankingUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getKPIDataUsageReport(props = {}) {
  const defaultUrl = API_ENDPOINTS.KPIDataUsageReportUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function accessHistory(props = {}) {
  const defaultUrl = API_ENDPOINTS.accessHistoryUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getSourceTables(props = {}) {
  const defaultUrl = API_ENDPOINTS.sourceTablesUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getAzureADGroupDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.azureADGroupUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getAllCommentsCount(props = {}) {
  const defaultUrl = API_ENDPOINTS.getAllCommentsUrl;

  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?app_id=` + params?.app_id;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function getComments(props = {}) {
  const defaultUrl = API_ENDPOINTS.commentsUrl;

  const { params = {}, headers = {} } = props;
  const urlWithParams =
    defaultUrl +
    `?app_id=` +
    params?.app_id +
    `&offset=` +
    params?.offset +
    `&limit=` +
    params?.limit;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function addComments(props = {}) {
  const defaultUrl = API_ENDPOINTS.commentsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function updateComments(props = {}) {
  const defaultUrl = API_ENDPOINTS.commentsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function deleteComments(props = {}) {
  const defaultUrl = API_ENDPOINTS.commentsUrl;

  const { payload = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + payload.queryParams;
  const promise = instance.delete(urlWithParams, payload, { headers });
  return promise;
}

export function likeComment(props = {}) {
  const defaultUrl = API_ENDPOINTS.commentUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getRecommendedApps(props = {}) {
  const defaultUrl = API_ENDPOINTS.recommendedAppsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function handleIrrelevantApps(props = {}) {
  const defaultUrl = API_ENDPOINTS.irrelevantAppsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function reRequestAppApproval(props = {}) {
  const defaultUrl = API_ENDPOINTS.reRequestUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchSampleData(props = {}) {
  const defaultUrl = API_ENDPOINTS.sampleDataUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getSigviewViewName(props = {}) {
  const defaultUrl = API_ENDPOINTS.getSigviewViewName;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getAllTickets(props = {}) {
  const defaultUrl = API_ENDPOINTS.allTicketsUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getTicketTemplate(props = {}) {
  const defaultUrl = API_ENDPOINTS.ticketTemplateUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getTicketDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.getTicketDetailsUrl;

  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?ticket_key=` + params?.ticket_key;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function fetchUpdateHelpCenterFeedback(props = {}) {
  const defaultUrl = API_ENDPOINTS.feedbackUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function raiseJiraTicket(props = {}) {
  const defaultUrl = API_ENDPOINTS.getTicketDetailsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function triggerFeedbackMail(props = {}) {
  const defaultUrl = API_ENDPOINTS.feedbackMailUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getUsersList(props = {}) {
  const defaultUrl = API_ENDPOINTS.getAllUsersUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function triggerIncidentMail(props = {}) {
  const defaultUrl = API_ENDPOINTS.incidentMailUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getAllApps(props = {}) {
  const defaultUrl = API_ENDPOINTS.allAppsUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function commentOnTicket(props = {}) {
  const defaultUrl = API_ENDPOINTS.ticketCommentUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function updateApplications(props = {}) {
  const defaultUrl = API_ENDPOINTS.reportsUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getBrandMaintainenceData(props = {}) {
  const defaultUrl = API_ENDPOINTS.brandMaintainenceUrl;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getBrandMaintenanceStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.brandMaintenanceStatusUrl;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function postBrandMaintainenceData(props = {}) {
  const defaultUrl = API_ENDPOINTS.brandMaintainenceUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function putBrandMaintainenceData(props = {}) {
  const defaultUrl = API_ENDPOINTS.brandMaintainenceUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function getPowerBiAccess(props = {}) {
  const defaultUrl = API_ENDPOINTS.getpowerBiAccessUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function searchConversations(props = {}) {
  const defaultUrl = API_ENDPOINTS.searchConversationsUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getSpecificConversation(props = {}) {
  const defaultUrl = API_ENDPOINTS.getSpecificConversationUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function updateConversation(props = {}) {
  const defaultUrl = API_ENDPOINTS.updateConversationUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function updateQueryDetail(props = {}) {
  const defaultUrl = API_ENDPOINTS.updateQueryDetailUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}
export function loginPowerBi(props = {}) {
  const defaultUrl = API_ENDPOINTS.powerBiLogin;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getPowerBiRefreshToken(props = {}) {
  const defaultUrl = API_ENDPOINTS.powerBiRefreshUrl;

  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchKPIInitialData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getKPIInitialDataUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function fetchSpecificEventData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getEventValueUrl;
  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function getValidatedData(formData, app_id) {
  const defaultUrl = API_ENDPOINTS.ColumnDefinitionUrl;
  const url = `${defaultUrl}/${app_id}`;

  return axios.post(url, formData, {
    withCredentials: true,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function updateColumnDefinitionData(props = {}) {
  const defaultUrl = API_ENDPOINTS.ColumnDefinitionUrl;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}

export function dowloadColumnDefinitionData(app_id, file_type) {
  const payload = {
    app_id: app_id,
    file_type: file_type,
  };

  const defaultUrl = API_ENDPOINTS?.downloadFileUrl;

  const promise = instance.post(defaultUrl, payload, {
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return promise;
}

export function getColumnDefinitionData(props = {}) {
  const defaultUrl = API_ENDPOINTS.ColumnDefinitionUrl;
  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?app_id=` + params?.app_id;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function fetchReusabilitySpecificEventData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getReusabilityEventValueUrl;
  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function uploadImage(formData) {
  const url = `${API_ENDPOINTS.imageuploadv2Url}`;
  return axios.post(url, formData, {
    withCredentials: true, // This is equivalent to credentials: 'include' in fetch
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function fetchDataCatalogTemplate(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataCatalogTemplateUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, signal } = props;
  const promise = instance.get(url, { signal, headers });
  return promise;
}

export function fetchSpecificDataCatalogs(props = {}) {
  const defaultUrl = API_ENDPOINTS.specificDataCatalogUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataProductDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataProductLandingUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}
export function getDataGovernance(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataGovernance;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function updateDataSourceDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.updateDataSource;
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function fetchRapdidUserLandingPage(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidUserLandingPageUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.get(url, payload, { signal, headers });
  return promise;
}

export function fetchRapdidUserSharedRapidApp(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidUserSharedAppsUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.get(url, payload, { signal, headers });
  return promise;
}
export function fetchRapdidAdminLandingPage(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidAdminLandingPageUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.get(url, payload, { signal, headers });
  return promise;
}

export function fetchRapdidRegisterTemplate(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidRegisterTemplateUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.get(url, payload, { signal, headers });
  return promise;
}

export function submitRapidApplicationRequest(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidSubmitApplicationUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function submitEditRapidApplicationRequest(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidSubmiEditApplicationUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function rapidSaveAsDraft(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidSaveAsDraftUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

// export function fetchRapidApplication(props = {}) {
//   const defaultUrl = API_ENDPOINTS.rapidEditApplicationUrl;
//   // Making variables from props
//   const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
//   const promise = instance.post(url, payload, { signal, headers });
//   return promise;
// }

export function fetchRapidApplication(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidEditApplicationUrl;
  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?request_id=` + params?.register_id;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function approveRejectRapidApplication(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidApproveRejectApplicationUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.patch(url, payload, { signal, headers });
  return promise;
}

export function checkAppUrlUnique(props = {}) {
  const defaultUrl = API_ENDPOINTS.checkAppURLUnique;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function rapidTrackStatus(props = {}) {
  const defaultUrl = API_ENDPOINTS.rapidTrackStatusUrl;
  // Making variables from props
  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?request_id=` + params?.register_id;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function getAllPublishedReports(props = {}) {
  const defaultUrl = API_ENDPOINTS.getReportListUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getAllPublishedStandaloneApps(props = {}) {
  const defaultUrl = API_ENDPOINTS.getStandaloneAppListUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}
export function getAllTags(props = {}) {
  const defaultUrl = API_ENDPOINTS.getTagsUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getAllRegisteredDataset(props = {}) {
  const defaultUrl = API_ENDPOINTS.getRegisteredDatasetUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}
export function getAllTables(props = {}) {
  const defaultUrl = API_ENDPOINTS.getAllTablesUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getAllWorkspaces(props = {}) {
  const defaultUrl = API_ENDPOINTS.getAllWorkspacesUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getSigviewAutomationLandigPageData(props = {}) {
  const defaultUrl = API_ENDPOINTS.sigviewAutomationLandingPageAPI;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getSigviewAutomationMetaJson(props = {}) {
  const defaultUrl = API_ENDPOINTS.sigviewAutomationMetaJsonAPI;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function submitSigviewJsonMetaData(props = {}) {
  const defaultUrl = API_ENDPOINTS.sigviewAutomationSubmit;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getSigviewColumnDefinition(props = {}) {
  const defaultUrl = API_ENDPOINTS.getSigviewTableColumnDef;
  // Making variables from props
  const { params = {}, headers = {} } = props;
  const urlWithParams = defaultUrl + `?table_id=` + params?.table_id;
  const promise = instance.get(urlWithParams, { headers });
  return promise;
}

export function dowloadTableColumnDefinitionData(app_id, file_type) {
  const payload = {
    table_id: app_id,
    file_type: file_type,
  };

  const defaultUrl = API_ENDPOINTS?.sigviewTableColumnDefDownload;

  const promise = instance.post(defaultUrl, payload, {
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return promise;
}

export function getValidatedSigviewTableData(formData, table_id) {
  const defaultUrl = API_ENDPOINTS.getSigviewTableColumnDef;
  const url = `${defaultUrl}/${table_id}`;

  return axios.post(url, formData, {
    withCredentials: true,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function updateSigviewColumnDefinitionData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getSigviewTableColumnDef;

  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.put(url, payload, { headers });
  return promise;
}
const getSherlockTokenHeader = () => {
  return {};
};

export function createSherlockAssistNewSession(payload) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistNewSession;
  const headers = getSherlockTokenHeader();
  const promise = instance.post(defaultUrl, payload, { headers });
  return promise;
}

export function sendMessageToSherlockAssist(session_id, payload) {
  const defaultUrl =
    API_ENDPOINTS.sherlockAssistSendQuery + session_id + "/send";
  const headers = getSherlockTokenHeader();
  const promise = instance.post(defaultUrl, payload, { headers });
  return promise;
}

export function checkQuerySherlockAssist(jobID) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistJobStatus + jobID;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function getSherlockSessionMessages(sessionID) {
  const defaultUrl =
    API_ENDPOINTS.sherlockAssistSessionMessage + sessionID + "/messages";
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function getSherlockAssitAllSessions() {
  const defaultUrl = API_ENDPOINTS.sherlockAssistAllSessions;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function reactToSherlockAssistResponse(payload, messageID) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistReact + messageID + "/react";
  const headers = getSherlockTokenHeader();
  const promise = instance.post(defaultUrl, payload, { headers });
  return promise;
}
export function sherlockAssistSearch(query) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistSearchUrl + "?query=" + query;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function sherlockAssistHealthCheck() {
  const defaultUrl = API_ENDPOINTS.sherlockAssistDatabricksHealthCheck;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function addSherlockAssistSessionFilters(session_id, payload) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistAddFilter + session_id;
  const headers = getSherlockTokenHeader();
  const promise = instance.put(defaultUrl, payload, { headers });
  return promise;
}

export function sherlockAssistDepenedentFilter(parent_filter, parent_values) {
  const defaultUrl =
    API_ENDPOINTS.sherlockAssistStateDepenedentFilter +
    "?parent_filter=" +
    parent_filter +
    "&parent_values=" +
    parent_values;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function sendQueryToSherlockAssist(payload) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistSendMessage;
  const headers = getSherlockTokenHeader();
  const promise = instance.post(defaultUrl, payload, { headers });
  return promise;
}

export function sendContinueQueryToSherlockAssist(payload) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistSendContinueMessage;
  const headers = getSherlockTokenHeader();
  const promise = instance.post(defaultUrl, payload, { headers });
  return promise;
}

// Ops page gets access pending requests
export function getOpsPendingRequests(props = {}) {
  // NOTE: key name in config is `opspendingpageUrl`
  const defaultUrl = API_ENDPOINTS.opspendingpageUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

// Ops page gets user list and roles
export function getOpsAccess(props = {}) {
  // NOTE: key name in config is `opsAccessUrl`
  const defaultUrl = API_ENDPOINTS.opsAccessUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function getEditedOpsAccess(props = {}) {
  // NOTE: key name in config is `opsAccessUrl`
  const defaultUrl = API_ENDPOINTS.opsEditedAccessUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

// Ops page submits roles
export function submitOpsRoles(props = {}) {
  // NOTE: key name in config is `opsSubmitRolesUrl`
  const defaultUrl = API_ENDPOINTS.opsSubmitRolesUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(defaultUrl, payload, { headers });
}

// Ops page submits roles
export function submitOpsEditRoles(props = {}) {
  // NOTE: key name in config is `opsEditSubmitRolesUrl`
  const defaultUrl = API_ENDPOINTS.opsEditSubmitRolesUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(defaultUrl, payload, { headers });
}

// Get UseCases for AI Model Selectors
export function getUseCases(props = {}) {
  const defaultUrl = API_ENDPOINTS.getUseCasesUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const state = store.getState();
  const email = state?.auth?.metadata?.email;
  const token = state?.auth?.token;
  const mergedHeaders = { ...headers };
  if (email) mergedHeaders["userEmail"] = email;
  if (token) mergedHeaders["authorization"] = `Bearer ${token}`;
  const promise = instance.get(url, { headers: mergedHeaders });
  return promise;
}

// Search Models for AI Model Selector
export function searchModels(props = {}) {
  const defaultUrl = API_ENDPOINTS.searchModelsUrl;
  // Making variables from props
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  const state = store.getState();
  const email = state?.auth?.metadata?.email;
  const token = state?.auth?.token;
  const mergedHeaders = { ...headers };
  if (email) mergedHeaders["userEmail"] = email;
  if (token) mergedHeaders["authorization"] = `Bearer ${token}`;
  const promise = instance.post(url, payload, { headers: mergedHeaders });
  return promise;
}

export function getUserMonitoringDashboardUrl(props = {}) {
  const state = store.getState();
  const email = state?.auth?.metadata?.email;
  const defaultUrl = `${API_ENDPOINTS.monitoringDashboardUrl}?email=${email}&role=user&return_url=true`;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}
export function getMonitoringDashboardUrl(props = {}) {
  const state = store.getState();
  const email = state?.auth?.metadata?.email;
  const defaultUrl = `${API_ENDPOINTS.monitoringDashboardUrl}?email=${email}&role=admin&return_url=true`;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function getUserBillingDashboardUrl(props = {}) {
  const state = store.getState();
  const email = state?.auth?.metadata?.email;
  const role = state?.auth?.metadata?.role;
  const defaultUrl = `${API_ENDPOINTS.billingDashboardUrl}?email=${email}&role=${role}&return_url=true`;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function getSpotlightData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getSpotlightDataUrl;

  // Making variables from props
  const { url = defaultUrl, headers = {} } = props;
  const promise = instance.get(url, { headers });
  return promise;
}

export function getDataProductGroupBy(props = {}) {
  const defaultUrl = API_ENDPOINTS.getDataProductGroupByUrl;

  // Making variables from props
  const { url = defaultUrl, payload = {}, headers = {} } = props;
  const promise = instance.post(url, payload, { headers });
  return promise;
}

export function sherlockAssistGetSessionFilter(session_id) {
  const defaultUrl = API_ENDPOINTS.sherlockAssistSessionFilters + session_id;
  const headers = getSherlockTokenHeader();
  const promise = instance.get(defaultUrl, { headers });
  return promise;
}

export function getApplicationData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getReportDataUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataProductApplicationData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getDataProductAppDataUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataCatalogData(props = {}) {
  const defaultUrl = API_ENDPOINTS.getDataCatalogDataUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function SubmitReview(props = {}) {
  const defaultUrl = API_ENDPOINTS.submitReviewURL;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataProductData(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataProductUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataProductDetailsData(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataProductDetailUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getDataCoverageDetails(props = {}) {
  const defaultUrl = API_ENDPOINTS.dataCoverageUrl;
  const { url = defaultUrl, headers = {}, payload = {}, signal } = props;
  const promise = instance.post(url, payload, { signal, headers });
  return promise;
}

export function getEditedResources(props = {}) {
  const defaultUrl = API_ENDPOINTS.opsEditedResourcesUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function rotateApiKey(props = {}) {
  const defaultUrl = API_ENDPOINTS.rotateApiKeyUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function generateApiKey(props = {}) {
  const defaultUrl = API_ENDPOINTS.generateApiKeyUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function fetch_lineageData(props = {}) {
  const defaultUrl = API_ENDPOINTS.newLineageUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

// Brain 2.0 APIs Call
export function getBrainDashboard(props = {}) {
  const defaultUrl = API_ENDPOINTS.brainDashboardUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function fetchBrainSubmissionsData(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchBrainSubmissionsDataUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function fetchIndividualSubmissionData(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainSubmissionsDataUrl}/${submission_id}`;
  return instance.get(defaultUrl);
}

export function fetchBrainApprovalData(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchBrainApprovalDataUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function fetchIndividualApproverSubmission(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainApprovalDataUrl}/${submission_id}`;
  return instance.get(defaultUrl);
}

export function fetchBrainAccessManagement(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchBrainAccessManagementUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function updateBrainAccessManagement(props = {}) {
  const defaultUrl = API_ENDPOINTS.updateBrainAccessManagementUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function getBrainReviewData(
  page,
  page_size,
  filter,
  search,
  sortColumn,
  direction,
  filter_by_low_confidence
) {
  const url = `${API_ENDPOINTS.reviewDataUrl}?page=${page}&page_size=${page_size}`;
  const payload = {
    sort_column: sortColumn === "" ? "" : sortColumn,
    direction: direction,
    search: search,
    filter: {
      brand: filter?.brand ?? null,
      subBrand: filter?.subBrand ?? null,
      variant: filter?.variant ?? null,
      subSegment: filter?.subSegment ?? null,
    },
    filter_by_low_confidence: filter_by_low_confidence,
  };
  return instance.post(url, payload);
}

export function handleSubmitReviewData(props = {}) {
  const defaultUrl = API_ENDPOINTS.submitBrainReviewUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function handleValidateEmail(props = {}) {
  const defaultUrl = API_ENDPOINTS.validateEmail;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function fetchBrainFunctionAccess(props = {}) {
  const defaultUrl = API_ENDPOINTS.functionAccessUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function getReviewColumns(props = {}) {
  const defaultUrl = API_ENDPOINTS.reviewColumnsUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function assignApprover(props) {
  const defaultUrl = API_ENDPOINTS.assignApproverUrl + props.submission_id;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function resubmitSubmissionData(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainSubmissionsDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function deleteSubmission(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainSubmissionsDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.delete(url, payload, { headers });
}

export function submitReviewedSubmission(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainApprovalDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function getSSOTData(page, page_size, filter, search) {
  const url = `${API_ENDPOINTS.ssotUrl}?page=${page}&page_size=${page_size}`;
  const payload = {
    search: search,
    filter: {
      brand: filter?.brand ?? null,
      subBrand: filter?.subBrand ?? null,
      variant: filter?.variant ?? null,
      subSegment: filter?.subSegment ?? null,
    },
  };
  return instance.post(url, payload);
}

export function getBrainCompetitorData(page, page_size, filter, search) {
  const url = `${API_ENDPOINTS.competitorDataUrl}?page=${page}&page_size=${page_size}`;
  const payload = {
    search: search,
    filter: {
      brand: filter?.brand ?? null,
      subBrand: filter?.subBrand ?? null,
      variant: filter?.variant ?? null,
      subSegment: filter?.subSegment ?? null,
    },
  };
  return instance.post(url, payload);
}

export function updateBrainTeam(props) {
  const defaultUrl = API_ENDPOINTS.brainTeamUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function deleteBrainTeam(props) {
  const defaultUrl = `${API_ENDPOINTS.brainTeamUrl}/${props.team_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.delete(url, payload, { headers });
}

export function updateBrainUsers(props) {
  const defaultUrl = API_ENDPOINTS.brainusers;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function validateUserInput(props) {
  const defaultUrl = API_ENDPOINTS.brainInputValidationUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function downloadExcelData(props = {}) {
  const defaultUrl = API_ENDPOINTS.downloadReviewDataUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, {
    responseType: "blob",
  });
}

export function getSubmissionDashboardData(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.brainDashboardSubmissionUrl}/${submission_id}`;
  return instance.get(defaultUrl);
}

export function getUserReAssignList(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainSubmissionsDataUrl}/${submission_id}/reassign-options`;
  return instance.get(defaultUrl);
}

export function ReAssignUser(submission_id, payload) {
  const defaultUrl = `${API_ENDPOINTS.fetchBrainSubmissionsDataUrl}/${submission_id}/reassign`;
  return instance.patch(defaultUrl, payload);
}

export function getMyForms() {
  const defaultUrl = API_ENDPOINTS.my_form_url;
  return instance.get(defaultUrl);
}

export function getAllForms() {
  const defaultUrl = API_ENDPOINTS.all_form_url;
  return instance.get(defaultUrl);
}

export function submitForm(props) {
  const defaultUrl = API_ENDPOINTS.submit_form_url;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function approveRejectForm(props) {
  const defaultUrl = API_ENDPOINTS.approve_reject_form_url;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}
// Harbor Api calls

export function fetchHarborAccessManagement(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchHarborAccessManagementUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function fetchHarborSourcesList(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchHarborSources;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function updateHarborUsers(props) {
  const defaultUrl = API_ENDPOINTS.updateHarborUsers;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function fetchReviewColumns(props) {
  const defaultUrl = API_ENDPOINTS.fetchReviewColumns;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function getHarborReviewData(
  page,
  page_size,
  filter,
  search,
  sortColumn,
  direction,
  filter_by_low_confidence,
  countrySourceId
) {
  const url = `${API_ENDPOINTS.harborReviewDataUrl}?page=${page}&page_size=${page_size}`;

  const payload = {
    sort_column: sortColumn === "" ? "" : sortColumn,
    direction: direction,
    search: search,
    filter: {
      subType: filter?.subType ?? null,
      subSegment: filter?.subSegment ?? null,
      manufacturer: filter?.manufacturer ?? null,
      brand: filter?.brand ?? null,
      include: filter?.include ?? null,
    },
    filter_by_low_confidence: filter_by_low_confidence,
    country_source_id: countrySourceId,
  };
  return instance.post(url, payload);
}

export function downloadHarborExcelData(props = {}) {
  const defaultUrl = API_ENDPOINTS.downloadHarborReviewDataUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, {
    responseType: "blob",
  });
}

export function handleSubmitHarborReviewData(props = {}) {
  const defaultUrl = API_ENDPOINTS.submitHarborReviewUrl;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function fetchHarborSubmissionsData(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchHarborSubmissionsDataUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function deleteHarborSubmission(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchHarborSubmissionsDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.delete(url, payload, { headers });
}

export function fetchHarborIndividualSubmissionData(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.fetchHarborSubmissionsDataUrl}/${submission_id}`;
  return instance.get(defaultUrl);
}

export function resubmitHarborSubmissionData(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchHarborSubmissionsDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function handleHarborValidateEmail(props = {}) {
  const defaultUrl = API_ENDPOINTS.validateHarborEmail;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.post(url, payload, { headers });
}

export function fetchHarborApprovalData(props = {}) {
  const defaultUrl = API_ENDPOINTS.fetchHarborApprovalDataUrl;
  const { url = defaultUrl, headers = {} } = props;
  return instance.get(url, { headers });
}

export function assignHarborApprover(props) {
  const defaultUrl = API_ENDPOINTS.assignHrborApproverUrl + props.submission_id;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function fetchHarborIndividualApproverSubmission(submission_id) {
  const defaultUrl = `${API_ENDPOINTS.fetchHarborApprovalDataUrl}/${submission_id}`;
  return instance.get(defaultUrl);
}

export function submitHarborReviewedSubmission(props) {
  const defaultUrl = `${API_ENDPOINTS.fetchHarborApprovalDataUrl}/${props.submission_id}`;
  const { url = defaultUrl, headers = {}, payload = {} } = props;
  return instance.patch(url, payload, { headers });
}

export function getHarborSSOTData(
  page,
  page_size,
  filter,
  search,
  countrySourceId
) {
  const url = `${API_ENDPOINTS.harborSsotUrl}?page=${page}&page_size=${page_size}`;
  const payload = {
    search: search,
    filter: {
      subType: filter?.subType ?? null,
      subSegment: filter?.subSegment ?? null,
      manufacturer: filter?.manufacturer ?? null,
      brand: filter?.brand ?? null,
      include: filter?.include ?? null,
    },
    country_source_id: countrySourceId,
  };
  return instance.post(url, payload);
}

export function fetchKnowledgeHubTableList() {
  const url = API_ENDPOINTS.knowledgeHubTableList;
  return instance.get(url);
}

export function fetchKnowledgeHubTable({
  tableName = "",
  search = "",
  page = 1,
  pageSize = 100,
}) {
  let url = `${API_ENDPOINTS.knowledgeHubUrl}?table_name=${tableName}&page=${page}&page_size=${pageSize}`;
  if (search?.length > 0) {
    url += `&search=${search}`;
  }

  return instance.get(url);
}

export function fetchKnowledgeHubRowHistory({ tableName = "", rowId }) {
  const url = `${API_ENDPOINTS.knowledgeHubHistoryUrl}?table_name=${tableName}&id=${rowId}`;
  const payload = {
    table_name: tableName,
    row_id: rowId,
  };
  return instance.post(url, payload);
}

export function addtoKnowledgeHubTable({
  tableName = "",
  payload,
  edit = false,
  rowId,
}) {
  const url = `${API_ENDPOINTS.knowledgeHubUrl}?table_name=${tableName}${
    edit ? `&id=${rowId}` : ""
  }`;
  if (edit) {
    return instance.patch(url, payload);
  }
  return instance.post(url, payload);
}

export function deleteFromKnowledgeHubTable({ tableName = "", rowId }) {
  const url = `${API_ENDPOINTS.knowledgeHubUrl}?table_name=${tableName}&id=${rowId}`;
  return instance.delete(url);
}

export function uplaodHarborExcel({ file, countrySourceId }) {
  const url = `${API_ENDPOINTS.harborUploadExcel}`;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("country_source_id", countrySourceId);

  return instance.post(url, formData);
}
